import type { DocumentReference, Reference } from "@medplum/fhirtypes";
import { getTRPCClient } from "@repo/api";
import { vogRecordingGuid } from "@repo/documents/vog-recording";
import type { PatientDocumentId, PatientId, ResourcePointer, VisitId } from "@repo/domain";
import { asPatientId, asPractitionerId, asVisitId } from "@repo/domain";
import { codeSystemUrl } from "@repo/fhir-systems";
import { DOCUMENT_SOURCE_FILE_NAME_URL } from "@repo/fhir-systems/extensions";
import { PATIENT_DOCUMENT_ENTITY_ID_SYSTEM } from "@repo/fhir-systems/identifiers";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../../actions/define-write";
import { useCollection } from "../../core/hooks";
import { getDataStoreClient } from "../../core/provider";
import { DomainResultError } from "../../core/write-errors";
import type { DocumentReferenceRow } from "../../fhir/types";
import { MATLAB_DATA_CONTENT_TYPE } from "../../lib/document-content-types";
import { optimisticPatientReference, patientDomainId } from "../_patient-reference";
import { optimisticPractitionerReference, practitionerDomainId } from "../_practitioner-reference";
import { optimisticVisitExecutionReference, visitDomainId } from "../_visit-reference";
import { binaryIdFromAttachmentUrl } from "./binary-id";

export { binaryIdFromAttachmentUrl } from "./binary-id";

/**
 * PatientDocument — normalized read-model for a file filed against a patient.
 *
 * Never carries a URL: a stored row's is presigned and expires within the hour,
 * so the id is what survives being cached. See [[binaryIdFromAttachmentUrl]].
 */
export interface PatientDocumentDto {
  id: PatientDocumentId;
  patientId: PatientId;
  visitId?: VisitId;
  classification?: string;
  /**
   * Absent only while the upload is still in flight: the Binary is minted
   * server-side, so an optimistic row cannot know it. Anything that reads the
   * bytes — view, download — must wait for it.
   */
  binaryId?: string;
  /** True while this row is an optimistic placeholder for an in-flight upload. */
  isPending: boolean;

  contentType: string;
  title: string;
  /** Exact source basename, independent of the display title. */
  sourceFileName?: string;
  size?: number;
  uploadedAt: string;
  uploadedBy: ResourcePointer;
}

const COLLECTION = "documentReference";

const DOCUMENT_STATUSES: ReadonlySet<string> = new Set(["current", "entered-in-error"]);

/**
 * The uploader, by identity. Both a stored row and an optimistic one name the
 * author by identifier — the locator beside it is FHIR's, resolved per
 * transaction, and says nothing about who filed the document.
 */
function uploader(author: Reference | undefined): ResourcePointer | undefined {
  const practitionerId = practitionerDomainId(author);
  if (practitionerId) return { resourceType: "Practitioner", id: asPractitionerId(practitionerId) };
  const patientId = patientDomainId(author);
  if (patientId) return { resourceType: "Patient", id: asPatientId(patientId) };
  return undefined;
}

function toDto(document: DocumentReferenceRow): PatientDocumentDto | undefined {
  const attachment = document.content?.[0]?.attachment;
  const binaryId = binaryIdFromAttachmentUrl(attachment?.url);
  const uploadedBy = uploader(document.author?.[0]);
  const patientId = document.patientId ?? patientDomainId(document.subject);
  const uploadedAt = attachment?.creation ?? document.date;
  const title = attachment?.title ?? document.description;
  const sourceFileName = attachment?.extension?.find(
    (entry) => entry.url === DOCUMENT_SOURCE_FILE_NAME_URL,
  )?.valueString;
  const status = document.status;
  // A pending row has no Binary yet. Every other row must have a parsable one:
  // dropping those is what makes a url the parser cannot read visible as a
  // missing document rather than as a row that quietly renders wrong.
  const isPending = !document.id && !attachment?.url;
  if (!document.domainId || !patientId || !uploadedBy || !uploadedAt || !title) return undefined;
  if (!binaryId && !isPending) return undefined;
  if (!attachment?.contentType || !status || !DOCUMENT_STATUSES.has(status)) return undefined;
  const visitId = document.visitId ?? visitDomainId(document.context?.encounter?.[0]);
  // `category`, not `type`: the purpose the document was filed for. `type` is
  // the LOINC document kind and we never write one.
  const classification = document.category?.flatMap((entry) => entry.coding ?? [])[0]?.code;
  return {
    id: document.domainId as PatientDocumentId,
    patientId: patientId as PatientId,
    ...(visitId ? { visitId: visitId as VisitId } : {}),
    ...(classification ? { classification } : {}),
    ...(binaryId ? { binaryId } : {}),
    isPending,
    contentType: attachment.contentType,
    title,
    ...(sourceFileName !== undefined ? { sourceFileName } : {}),
    ...(attachment.size !== undefined ? { size: attachment.size } : {}),
    uploadedAt,
    uploadedBy,
  };
}

/**
 * The patient's document list, newest first: every classification, retracted
 * ones dropped. Classification is a row label, never a filter. The one content
 * exceptions are MATLAB and calibrated CSV companions: they accompany video playback rather than
 * standing alone in the Documents tab. The trace accessor includes it. A row we cannot
 * project — no identity, no parsable binary — is skipped rather than taking the
 * query down.
 *
 * The sort lives here rather than in the query because it is what every caller
 * means by "the patient's documents": a chart is read from the most recent
 * entry backwards, and an in-flight upload belongs where the reader is looking.
 */
export function projectPatientDocuments(rows: DocumentReferenceRow[]): PatientDocumentDto[] {
  return projectPatientDocumentsForTraceLookup(rows).filter(
    (document) =>
      document.contentType !== MATLAB_DATA_CONTENT_TYPE && !vogRecordingGuid(document.sourceFileName, "calibrated"),
  );
}

/** Current documents including hidden tracking data, for the player's GUID lookup. */
export function projectPatientDocumentsForTraceLookup(rows: DocumentReferenceRow[]): PatientDocumentDto[] {
  return rows
    .flatMap((row) => {
      const dto = toDto(row);
      return dto && row.status === "current" ? [dto] : [];
    })
    .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}

interface UsePatientDocumentsParams {
  patientId?: string;
}

interface UsePatientDocumentsResult {
  documents: PatientDocumentDto[];
  isLoading: boolean;
  isReady: boolean;
  isError: boolean;
}

export function usePatientDocuments(params?: UsePatientDocumentsParams): UsePatientDocumentsResult {
  return usePatientDocumentQuery(params, projectPatientDocuments);
}

/** Includes tracking companions hidden from the Documents tab. Feed these documents
 * to findVogTraceDocument, then read the match through /documents/:id/content.
 */
export function usePatientDocumentsForTraceLookup(params?: UsePatientDocumentsParams): UsePatientDocumentsResult {
  return usePatientDocumentQuery(params, projectPatientDocumentsForTraceLookup);
}

function usePatientDocumentQuery(
  params: UsePatientDocumentsParams | undefined,
  project: (rows: DocumentReferenceRow[]) => PatientDocumentDto[],
): UsePatientDocumentsResult {
  const patientId = params?.patientId;
  // Without a patient there is nothing to ask for, and consuming a collection
  // is what starts its subscription and full fetch — for the rest of the
  // session. The app shell renders a breadcrumb on every route, so a hook that
  // consumed unconditionally pulled every DocumentReference in the project on
  // every page of the app.
  const enabled = patientId !== undefined;
  const collection = useCollection(COLLECTION, { enabled });

  const result = useLiveQuery({
    queryKey: [collection.id, "patient-documents", patientId ?? null],
    query: (q) => {
      if (patientId) {
        return q
          .from({ d: collection })
          .where(({ d }) => eq(d.subject?.identifier?.value, patientId))
          .fn.select(({ d }) => ({ row: d }));
      }
      return undefined;
    },
  });

  const rows = ((result.data as Array<{ row?: DocumentReference }> | undefined) ?? []).flatMap((entry) =>
    entry.row ? [entry.row as DocumentReferenceRow] : [],
  );

  return {
    documents: project(rows),
    isLoading: result.isLoading,
    isReady: result.isReady,
    isError: result.isError,
  };
}

function documents() {
  return getDataStoreClient().getCollection(COLLECTION);
}

/**
 * The purposes the server files under, spelled to match
 * `patient-document.codec.ts`. Kept local rather than imported: `@repo/api`'s
 * codec is server code, and the optimistic row needs the code on the client so
 * the badge is right the instant the row appears rather than after the refetch.
 */
const PURPOSE = { patientRecord: "patient-record", observationCapture: "observation-capture" } as const;

interface FileDocumentParams {
  id: string;
  patientId: string;
  file: File;
  /** The optimistic row's author; the server uses the authenticated profile. */
  uploader?: { resourceType: "Patient" | "Practitioner"; id: string };
  visitId?: string;
}

// Downloads remain pending until sync supplies the server-minted Binary id.
function insertPendingDocument(params: FileDocumentParams, purpose: string): void {
  const optimisticDocumentId = params.id;
  documents().insert({
    resourceType: "DocumentReference",
    identifier: [{ system: PATIENT_DOCUMENT_ENTITY_ID_SYSTEM, value: optimisticDocumentId }],
    domainId: optimisticDocumentId,
    status: "current",
    subject: optimisticPatientReference(params.patientId),
    patientId: params.patientId,
    ...(params.visitId
      ? {
          context: { encounter: [optimisticVisitExecutionReference(asVisitId(params.visitId))] },
          visitId: params.visitId,
        }
      : {}),
    author: params.uploader
      ? [
          params.uploader.resourceType === "Patient"
            ? optimisticPatientReference(params.uploader.id)
            : optimisticPractitionerReference(params.uploader.id),
        ]
      : undefined,
    date: new Date().toISOString(),
    category: [{ coding: [{ system: codeSystemUrl("patient-document-classification"), code: purpose }] }],
    content: [
      {
        attachment: {
          // No url: there is no Binary until the server makes one.
          contentType: params.file.type || "application/octet-stream",
          title: params.file.name,
          size: params.file.size,
          creation: new Date().toISOString(),
        },
      },
    ],
  });
}

function uploadForm(params: FileDocumentParams): FormData {
  const form = new FormData();
  form.set("id", params.id);
  form.set("file", params.file);
  form.set("patientId", params.patientId);
  form.set("title", params.file.name);
  if (params.visitId) form.set("visitId", params.visitId);
  return form;
}

export type UploadPatientRecordParams = FileDocumentParams & { uploader: NonNullable<FileDocumentParams["uploader"]> };

/** Intent: "file this existing material against the patient, to be read". */
export const uploadPatientRecord = defineWrite<UploadPatientRecordParams>({
  timeoutMs: null,
  optimistic: (params) => insertPendingDocument(params, PURPOSE.patientRecord),
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.document.uploadRecord.mutate(uploadForm(params), options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/** A capture belongs to the visit it was taken during, so the visit is required. */
export type CaptureObservationDocumentParams = FileDocumentParams & { visitId: string };

/** Intent: "this content IS a finding, captured while observing". */
const captureDocument = defineWrite<CaptureObservationDocumentParams & { onSaved?: (id: PatientDocumentId) => void }>({
  timeoutMs: null,
  optimistic: (params) => insertPendingDocument(params, PURPOSE.observationCapture),
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.document.captureObservation.mutate(uploadForm(params), options);
    if (!result.success) throw new DomainResultError(result.error.message);
    params.onSaved?.(result.data.patientDocumentId);
  },
});

export function captureObservationDocument(
  params: CaptureObservationDocumentParams,
  onSaved?: (id: PatientDocumentId) => void,
) {
  return captureDocument({ ...params, onSaved });
}

/**
 * One document by its domain id. The collection is the patient's whole list
 * either way — this is the lookup spelling, for callers that want a single
 * document rather than the list.
 */
export function usePatientDocument(params?: { patientId: string; documentId: string }): PatientDocumentDto | undefined {
  const { documents } = usePatientDocuments(params ? { patientId: params.patientId } : undefined);
  return params ? documents.find((candidate) => candidate.id === params.documentId) : undefined;
}

const retractDocument = defineWrite<PatientDocumentId>({
  optimistic: (id) => {
    documents().update(id, (draft) => {
      draft.status = "entered-in-error";
    });
  },
  persist: async (patientDocumentId, options) => {
    const result = await getTRPCClient().charting.document.retract.mutate({ patientDocumentId }, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

export async function retractPatientDocument(documentId: PatientDocumentId): Promise<void> {
  await retractDocument(documentId).isPersisted.promise;
}
